import { ChangeDetectionStrategy, Component } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { AdvancedReportConfig } from '@app/advanced-reports/models/advanced-report-config';
import { TabExportData } from '@app/advanced-reports/dynamic-reporting/advanced-reports-excel-export.service';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { BehaviorSubject, EMPTY, Observable, of } from 'rxjs';
import { map, shareReplay, switchMap, take } from 'rxjs/operators';
import { getIntQueryParam } from '@utils/route-utils';
import { pensionContributionsGridColumns } from './pension-contributions-grid-columns';
import { pensionPaymentErrorsGridColumns } from './pension-payment-errors-grid-columns';
import { PensionContributionsDataProvider } from './state/pension-contributions.data-provider';
import { PensionContributionsResponse } from './state/pension-contribution-employee.model';
import { TabConfig } from '@design/buttons/tab-list/tab-config';
import { GridApi } from '@ag-grid-community/core';
import { combineLatest, firstValueFrom } from 'rxjs';

type TabView = 'Contributions' | 'Payment errors' | 'Submission errors';

@UntilDestroy()
@Component({
  templateUrl: './pension-contributions-advanced-view.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [PensionContributionsDataProvider],
  styleUrls: ['./pension-contributions-advanced-view.component.scss'],
  standalone: false
})
export class PensionContributionsAdvancedViewComponent {
  protected readonly config$: Observable<AdvancedReportConfig>;
  protected readonly hasErrors$: Observable<boolean>;
  protected readonly rowData$: Observable<any[]>;
  protected readonly tabs$: Observable<TabConfig[]>;
  protected readonly currentView$: Observable<TabView>;

  private readonly submissionId: number | null;
  private readonly response$: Observable<PensionContributionsResponse>;
  private readonly currentViewSubject = new BehaviorSubject<TabView>('Contributions');
  private readonly contributionsData$: Observable<any[]>;
  private readonly paymentErrorsData$: Observable<any[]>;
  private readonly submissionsErrorsData$: Observable<any[]>;

  constructor(
    private readonly route: ActivatedRoute,
    private readonly dataProvider: PensionContributionsDataProvider
  ) {
    this.submissionId = getIntQueryParam(this.route, 'submissionId');
    this.response$ = this.initializeResponse();
    this.hasErrors$ = this.initializeHasErrors();

    this.contributionsData$ = this.initializeContributionsData();
    this.paymentErrorsData$ = this.initializePaymentErrorsData();
    this.submissionsErrorsData$ = this.initializeSubmissionsErrorsData();

    this.rowData$ = this.initializeRowDataForCurrentView();
    this.config$ = this.initializeConfig();
    this.currentView$ = this.currentViewSubject.asObservable();
    this.tabs$ = this.createTabsBasedOnData();
  }

  getTabExportData = async (tabLabel: string): Promise<Partial<TabExportData> | null> => {
    let rowData$: Observable<any[]>;
    let gridColumns: any[] = [];

    switch (tabLabel) {
      case 'Contributions':
        rowData$ = this.contributionsData$;
        gridColumns = pensionContributionsGridColumns();
        break;
      case 'Payment errors':
        rowData$ = this.paymentErrorsData$;
        gridColumns = pensionPaymentErrorsGridColumns();
        break;
      case 'Submission errors':
        rowData$ = this.submissionsErrorsData$;
        gridColumns = pensionPaymentErrorsGridColumns();
        break;
      default:
        rowData$ = this.contributionsData$;
        gridColumns = pensionContributionsGridColumns();
    }

    const rowData = await firstValueFrom(rowData$);

    return {
      tabLabel,
      rowData,
      gridColumns
    };
  };

  private initializeResponse(): Observable<PensionContributionsResponse> {
    if (this.submissionId === null) {
      return EMPTY;
    }

    return this.dataProvider.getSubmissionContributions$(this.submissionId).pipe(take(1), untilDestroyed(this), shareReplay(1));
  }

  private initializeHasErrors(): Observable<boolean> {
    return this.response$.pipe(map((response) => response.contributions.some((c) => c.errorDescription)));
  }

  private initializeContributionsData(): Observable<any[]> {
    return this.response$.pipe(
      map((response) => {
        return response.contributions.slice().sort((a, b) => {
          if (a.errorDescription && !b.errorDescription) return -1;
          if (!a.errorDescription && b.errorDescription) return 1;
          return a.employeeName.localeCompare(b.employeeName);
        });
      })
    );
  }

  private initializePaymentErrorsData(): Observable<any[]> {
    if (this.submissionId === null) {
      return of([]);
    }

    return this.dataProvider.getSubmissionPaymentErrors$(this.submissionId).pipe(shareReplay(1));
  }

  private initializeSubmissionsErrorsData(): Observable<any[]> {
    if (this.submissionId === null) {
      return of([]);
    }

    return this.dataProvider.getSubmissionSubmissionsErrors$(this.submissionId).pipe(shareReplay(1));
  }

  private initializeRowDataForCurrentView(): Observable<any[]> {
    return this.currentViewSubject.pipe(
      switchMap((view) => {
        switch (view) {
          case 'Contributions':
            return this.contributionsData$;
          case 'Payment errors':
            return this.paymentErrorsData$;
          case 'Submission errors':
            return this.submissionsErrorsData$;
          default:
            return this.contributionsData$;
        }
      })
    );
  }

  private initializeConfig(): Observable<AdvancedReportConfig> {
    return this.currentViewSubject.pipe(
      switchMap((view) => {
        return this.response$.pipe(
          map((response) => ({
            gridId: `pension-${view.toLowerCase().replace(/\s+/g, '-')}`,
            reportTitle: `Pensions connect / ${response.payrollName}`,
            reportType: 'standard' as const,
            gridColumns$: of(this.getColumnsForView(view))
          }))
        );
      })
    );
  }

  private getColumnsForView(view: TabView): any[] {
    switch (view) {
      case 'Contributions':
        return pensionContributionsGridColumns();
      case 'Payment errors':
        return pensionPaymentErrorsGridColumns();
      case 'Submission errors':
        return pensionPaymentErrorsGridColumns();
      default:
        return pensionContributionsGridColumns();
    }
  }

  private createTabsBasedOnData(): Observable<TabConfig[]> {
    return combineLatest([
      this.contributionsData$,
      this.paymentErrorsData$,
      this.submissionsErrorsData$,
      this.currentViewSubject.asObservable()
    ]).pipe(
      map(([contributions, paymentErrors, submissionsErrors, currentView]) => {
        const tabs: TabConfig[] = [];

        if (submissionsErrors.length > 0) {
          tabs.push({
            label: 'Submission errors',
            tid: 'pension-submissions-errors-tab',
            active: currentView === 'Submission errors',
            navigate: () => this.switchTab('Submission errors')
          });
        }
        if (paymentErrors.length > 0) {
          tabs.push({
            label: 'Payment errors',
            tid: 'pension-payment-errors-tab',
            active: currentView === 'Payment errors',
            navigate: () => this.switchTab('Payment errors')
          });
        }

        if (contributions.length > 0) {
          tabs.push({
            label: 'Contributions',
            tid: 'pension-contributions-tab',
            active: currentView === 'Contributions',
            navigate: () => this.switchTab('Contributions')
          });
        }

        return tabs;
      })
    );
  }

  private switchTab(view: TabView): void {
    this.currentViewSubject.next(view);
  }
}
